import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { toast } from "sonner";
import {
  Calendar, Plus, Trash, Bus, UserCircle, IdentificationCard,
  CalendarBlank, ListChecks, MagnifyingGlass, ArrowsCounterClockwise, Copy, X,
} from "@phosphor-icons/react";
import { MasterScheduleModal } from "@/components/ScheduleModals";

const REC_BADGE = {
  daily:   { label: "Daily",    color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  weekly:  { label: "Weekly",   color: "bg-blue-50 text-blue-700 border-blue-200" },
  monthly: { label: "Monthly",  color: "bg-purple-50 text-purple-700 border-purple-200" },
  yearly:  { label: "Yearly",   color: "bg-amber-50 text-amber-700 border-amber-200" },
  custom:  { label: "Custom",   color: "bg-slate-50 text-slate-700 border-slate-200" },
};
const DOW_LBL = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const mLbl = (m) => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recFilter, setRecFilter] = useState("All");
  const [expanded, setExpanded] = useState(null);
  const [cloneSrc, setCloneSrc] = useState(null); // schedule being cloned

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, b, d, c] = await Promise.all([
        api.get("/schedules"), api.get("/routes"), api.get("/fleet/buses"),
        api.get("/crew/drivers"), api.get("/crew/conductors"),
      ]);
      setSchedules(s.data); setRoutes(r.data); setBuses(b.data);
      setDrivers(d.data); setConductors(c.data);
    } catch (e) {
      toast.error("Failed to load schedules");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete master schedule "${s.name}"?\n\nThis will remove all ${s.trips_count} generated trips.`)) return;
    try {
      const { data } = await api.delete(`/schedules/${s.id}`);
      toast.success(`Deleted "${s.name}" · ${data.deleted_trips} trips removed`);
      setSchedules(p => p.filter(x => x.id !== s.id));
    } catch { toast.error("Failed to delete schedule"); }
  };

  const filtered = useMemo(() => schedules.filter(s => {
    const matchesText = !q || s.name?.toLowerCase().includes(q.toLowerCase()) || s.bus_id?.toLowerCase().includes(q.toLowerCase());
    const matchesRec = recFilter === "All" || s.recurrence === recFilter;
    return matchesText && matchesRec;
  }), [schedules, q, recFilter]);

  const totals = useMemo(() => ({
    schedules: schedules.length,
    trips: schedules.reduce((s, x) => s + (x.trips_count || 0), 0),
    active: schedules.filter(s => s.status === "Active").length,
  }), [schedules]);

  return (
    <div className="p-4 lg:p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="label-cap text-slate-500">Operations</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Master Schedules</h1>
          <div className="text-xs text-slate-500 mt-1">Bulk-generate trips across days, weeks, months or years · single bus + crew assignment</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="schedules-refresh-btn"
            onClick={load}
            className="px-3 py-2 border border-slate-200 text-slate-600 rounded-md text-sm hover:bg-slate-50 flex items-center gap-1.5"
            title="Refresh"
          >
            <ArrowsCounterClockwise size={14}/><span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            data-testid="schedules-new-btn"
            onClick={() => setOpen(true)}
            className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5"
          >
            <Plus size={14}/>New Master Schedule
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Kpi icon={Calendar}    label="Master Schedules" value={totals.schedules} accent="text-[#1E3A8A]" />
        <Kpi icon={ListChecks}  label="Total Trips"      value={totals.trips}     accent="text-emerald-600" />
        <Kpi icon={CalendarBlank} label="Active"         value={totals.active}    accent="text-amber-600" />
        <Kpi icon={Bus}         label="Buses In Use"     value={new Set(schedules.map(s => s.bus_id).filter(Boolean)).size} accent="text-purple-600" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-slate-400"/>
          <input
            data-testid="schedules-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by name or bus…"
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <select
          data-testid="schedules-recurrence-filter"
          value={recFilter}
          onChange={e => setRecFilter(e.target.value)}
          className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"
        >
          <option>All</option>
          {Object.keys(REC_BADGE).map(k => <option key={k} value={k}>{REC_BADGE[k].label}</option>)}
        </select>
        <span className="text-xs text-slate-500">Showing {filtered.length} of {schedules.length}</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center">
          <Calendar size={42} weight="duotone" className="text-slate-300 mx-auto mb-3"/>
          <div className="font-display font-bold text-lg text-slate-700">No master schedules yet</div>
          <div className="text-sm text-slate-500 mt-1 mb-4">Create your first master schedule to bulk-generate trips for days, weeks or months in advance.</div>
          <button
            data-testid="schedules-empty-create-btn"
            onClick={() => setOpen(true)}
            className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-1.5"
          >
            <Plus size={14}/>Create Master Schedule
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => {
            const rec = REC_BADGE[s.recurrence] || REC_BADGE.custom;
            const isOpen = expanded === s.id;
            return (
              <div key={s.id} data-testid={`schedule-row-${s.id}`} className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:items-center">
                  <div className="lg:col-span-4 min-w-0">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-[#1E3A8A] shrink-0"/>
                      <div className="font-semibold text-sm truncate">{s.name}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>{s.start_date} → {s.end_date}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${rec.color}`}>{rec.label}</span>
                      {s.recurrence === "weekly" && s.days_of_week?.length > 0 && (
                        <span className="text-[10px] text-slate-500">{s.days_of_week.map(d => DOW_LBL[d]).join(" ")}</span>
                      )}
                    </div>
                  </div>
                  <div className="lg:col-span-3 flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-slate-600"><Bus size={12}/>{s.bus_id || "—"}</span>
                    <span className="flex items-center gap-1 text-slate-600"><UserCircle size={12}/>{(s.driver_id || "—").split(" ")[0]}</span>
                    <span className="flex items-center gap-1 text-slate-600"><IdentificationCard size={12}/>{(s.conductor_id || "—").split(" ")[0]}</span>
                  </div>
                  <div className="lg:col-span-3 flex items-center gap-4 text-xs">
                    <div>
                      <div className="text-[10px] uppercase text-slate-400 font-semibold tracking-wide">Trips</div>
                      <div className="font-display font-bold text-base text-[#1E3A8A]">{s.trips_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-400 font-semibold tracking-wide">Dates</div>
                      <div className="font-display font-bold text-base text-slate-700">{s.dates_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-400 font-semibold tracking-wide">Routes</div>
                      <div className="font-display font-bold text-base text-slate-700">{(s.routes || []).length}</div>
                    </div>
                  </div>
                  <div className="lg:col-span-2 flex items-center justify-end gap-2">
                    <button
                      data-testid={`schedule-expand-${s.id}`}
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                      className="px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50"
                    >
                      {isOpen ? "Hide" : "View"}
                    </button>
                    <button
                      data-testid={`schedule-clone-${s.id}`}
                      onClick={() => setCloneSrc(s)}
                      className="px-2 py-1.5 text-xs border border-slate-200 rounded hover:bg-blue-50 text-[#1E3A8A] inline-flex items-center gap-1"
                      title="Clone schedule with new dates"
                    >
                      <Copy size={14}/>
                    </button>
                    <button
                      data-testid={`schedule-delete-${s.id}`}
                      onClick={() => remove(s)}
                      className="px-2 py-1.5 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50"
                      title="Delete schedule and its trips"
                    >
                      <Trash size={14}/>
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-200 bg-slate-50/50 p-4">
                    <div className="label-cap text-slate-500 mb-2">Daily Route Plan ({(s.routes || []).length} routes per active date)</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200">
                            <th className="py-2 pr-3">#</th>
                            <th className="py-2 pr-3">Route</th>
                            <th className="py-2 pr-3">Start</th>
                            <th className="py-2 pr-3">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(s.routes || []).map((r, i) => (
                            <tr key={i} className="border-b border-slate-100 last:border-0">
                              <td className="py-1.5 pr-3 font-mono text-slate-500">#{r.sequence}</td>
                              <td className="py-1.5 pr-3 font-mono font-semibold text-[#1E3A8A]">{r.route_id}</td>
                              <td className="py-1.5 pr-3">{mLbl(r.start_min)}</td>
                              <td className="py-1.5 pr-3">{r.duration_min} min</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 text-[11px] text-slate-500">
                      Created by <b>{s.created_by}</b> · {s.created_at?.split("T")[0]}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MasterScheduleModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => { load(); }}
        routes={routes} buses={buses} drivers={drivers} conductors={conductors}
      />
      <CloneScheduleModal
        src={cloneSrc}
        buses={buses}
        onClose={() => setCloneSrc(null)}
        onCloned={(c) => { setCloneSrc(null); load(); }}
      />
    </div>
  );
}

function CloneScheduleModal({ src, buses, onClose, onCloned }) {
  const depotMap = useDepots();
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", bus_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!src) return;
    // shift the date range forward by its own length so the clone starts after the source ends
    const s = new Date(src.start_date), e = new Date(src.end_date);
    const span = Math.max(0, Math.floor((e - s) / 86400000));
    const newStart = new Date(e); newStart.setDate(newStart.getDate() + 1);
    const newEnd = new Date(newStart); newEnd.setDate(newEnd.getDate() + span);
    setForm({
      name: `${src.name} · Copy`,
      start_date: newStart.toISOString().slice(0,10),
      end_date: newEnd.toISOString().slice(0,10),
      bus_id: src.bus_id || "",
    });
  }, [src]);

  if (!src) return null;

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Give the cloned schedule a name"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Pick a date range"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/schedules/${src.id}/clone`, form);
      toast.success(`Cloned "${data.name}" · ${data.expanded_trips} trips across ${data.dates_count} dates`);
      onCloned?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div data-testid="clone-schedule-modal" className="bg-white w-full max-w-md max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={e=>e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between z-10 rounded-t-2xl sm:rounded-t-lg">
          <div>
            <div className="flex items-center gap-2"><Copy size={18} className="text-[#1E3A8A]"/><h3 className="font-display text-xl font-bold">Clone Schedule</h3></div>
            <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[260px]">From: {src.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-slate-700">
            <div><b>Recurrence:</b> {src.recurrence}{src.recurrence === "weekly" && src.days_of_week?.length ? ` (${src.days_of_week.length} days/week)` : ""}</div>
            <div><b>Routes:</b> {(src.routes || []).length} routes per active day</div>
            <div className="text-slate-500 mt-1">Same routes & crew assignment will be reused — only the dates and name change below.</div>
          </div>
          <Lbl label="New schedule name *">
            <input data-testid="clone-name-input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
                   className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </Lbl>
          <div className="grid grid-cols-2 gap-2">
            <Lbl label="New start date">
              <input data-testid="clone-start-date" type="date" value={form.start_date} onChange={e=>setForm({...form, start_date:e.target.value})}
                     className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
            </Lbl>
            <Lbl label="New end date">
              <input data-testid="clone-end-date" type="date" value={form.end_date} onChange={e=>setForm({...form, end_date:e.target.value})}
                     className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
            </Lbl>
          </div>
          <Lbl label="Bus (override optional)">
            <select data-testid="clone-bus-select" value={form.bus_id} onChange={e=>setForm({...form, bus_id:e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
              <option value="">{`— Keep source bus (${src.bus_id || "none"}) —`}</option>
              {buses.map((b, idx) => <option key={`${b.fleet_id}__${idx}`} value={b.fleet_id}>{`${b.fleet_id} · ${fmtDepot(b.depot, depotMap)}`}</option>)}
            </select>
          </Lbl>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
          <button data-testid="clone-submit-btn" disabled={busy} onClick={submit} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
            <Copy size={14}/>{busy ? "Cloning…" : "Clone Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Lbl = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1">{label}</div>
    {children}
  </div>
);

const Kpi = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between">
    <div>
      <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">{label}</div>
      <div className={`font-display font-bold text-2xl mt-0.5 ${accent}`}>{value}</div>
    </div>
    <Icon size={28} weight="duotone" className={accent}/>
  </div>
);
